import moment from 'moment'
import api from '@/apis'
import Vuetify from '@/plugins/vuetify'

export default {
  state: {
    filters: {
      device_ids: [],
      dates: [new Date(Date.now() - 604800000), new Date(Date.now())],
      search: ''
    },
    table: {
      selected: null,
      loading: false,
      headers: [
        { text: Vuetify.locale.t('$vuetify.__Login.username'), value: 'name', sortable: false },
        { text: Vuetify.locale.t('$vuetify.__DatatableHeader.phone'), value: 'mobile', sortable: false },
        { text: Vuetify.locale.t('$vuetify.__DatatableHeader.tag'), value: 'tags', sortable: false },
        { text: '', value: 'edit', align: 'right', sortable: false }
      ],
      items: [],
      total_items: 0,
      options: { itemsPerPage: 5, page: 1 }
    },
    chatroom: {
      visibility: false,
      profile: null,
      sending: false,
      loading: false,
      session_id: null,
      items: []
    }
  },
  mutations: {
    SET_CHAT_FILTERS_DEVICE_IDS(state, payload) {
      state.filters.device_ids = payload
    },
    SET_CHAT_FILTERS_DATES(state, payload) {
      state.filters.dates = payload
    },
    SET_CHAT_FILTERS_SEARCH(state, payload) {
      state.filters.search = payload
    },

    SET_CHAT_TABLE_SELECTED(state, payload) {
      state.table.selected = payload
    },
    SET_CHAT_TABLE_LOADING(state, payload) {
      state.table.loading = payload
    },
    SET_CHAT_TABLE_ITEMS(state, payload) {
      state.table.items = payload
    },
    SET_CHAT_TABLE_TOTAL_ITEMS(state, payload) {
      state.table.total_items = payload
    },
    SET_CHAT_TABLE_OPTIONS(state, payload) {
      state.table.options = payload
    },
    SET_CHAT_TABLE_OPTIONS_PAGE(state, payload) {
      state.table.options.page = payload
    },

    SET_CHAT_CHATROOM_VISIBILITY(state, payload) {
      state.chatroom.visibility = payload
    },
    SET_CHAT_CHATROOM_PROFILE(state, payload) {
      state.chatroom.profile = payload
    },
    SET_CHAT_CHATROOM_SENDING(state, payload) {
      state.chatroom.sending = payload
    },
    SET_CHAT_CHATROOM_LOADING(state, payload) {
      state.chatroom.loading = payload
    },
    SET_CHAT_CHATROOM_ITEMS(state, payload) {
      state.chatroom.items = payload
    },
    SET_CHAT_CHATROOM_ID(state, payload) {
      state.chatroom.session_id = payload
    },
    APPEND_CHAT_CHATROOM_ITEMS(state, payload) {
      state.chatroom.items.push(payload)
    }
  },
  actions: {
    // Chat List
    async fetchChatList({ state, commit }, payload) {
      try {
        if (!(payload?.from === 'Falcon')) {
          if (!(payload?.from === 'Refresh')) commit('SET_CHAT_TABLE_ITEMS', [])
          commit('SET_CHAT_TABLE_LOADING', true)
        }

        const page = state.table.options.page
        const itemsPerPage = state.table.options.itemsPerPage

        const start_datetime = state.filters.dates[0] ? moment(state.filters.dates[0]).format('YYYY-MM-DD') : null
        const end_datetime = state.filters.dates[state.filters.dates.length - 1] ? moment(state.filters.dates[state.filters.dates.length - 1]).format('YYYY-MM-DD') : null

        let device_ids = state.filters.device_ids.toString()

        let params = { limit: itemsPerPage, offset: page * itemsPerPage - itemsPerPage }
        if (device_ids?.length > 0) params.device_ids = device_ids
        if (state.filters.search) params.search = state.filters.search
        if (start_datetime) params.start_datetime = start_datetime
        if (end_datetime) params.end_datetime = end_datetime

        const response = await api.getChatList(params)
        if (response.data.success) {
          commit('SET_CHAT_TABLE_ITEMS', response.data.data)
          commit('SET_CHAT_TABLE_TOTAL_ITEMS', response.data.total)
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      } finally {
        commit('SET_CHAT_TABLE_LOADING', false)
      }
    },

    // User Details & Messages
    async fetchChatDetail({ state, commit }) {
      try {
        commit('SET_CHAT_CHATROOM_VISIBILITY', false)
        commit('SET_CHAT_CHATROOM_PROFILE', null)
        commit('SET_SNACKBAR', { status: true, color: 'loading', text: Vuetify.locale.t('$vuetify.__SnackBar.loading', Vuetify.locale.t('$vuetify.__Chat.profile')), loading: true })

        const response = await api.getChatDetail({ session_id: state.table.selected?.session_id })
        if (response.data.success) {
          commit('SET_CHAT_CHATROOM_VISIBILITY', true)
          commit('SET_CHAT_CHATROOM_PROFILE', response.data.data)
          commit('SET_SNACKBAR', { status: true, color: 'success', text: Vuetify.locale.t('$vuetify.__SnackBar.load', ''), icon: 'check', timeout: '2000' })
        } else throw Error(response.data.message)
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      }
    },
    async fetchChatMessage({ state, commit }, payload) {
      try {
        if (!(payload?.from === 'Falcon')) {
          commit('SET_CHAT_CHATROOM_LOADING', true)
          commit('SET_CHAT_CHATROOM_ITEMS', [])
        }
        commit('SET_CHAT_CHATROOM_ID', state.table.selected?.session_id)

        const is_refresh = payload?.from === 'Falcon' && !!payload?.session_id && payload?.session_id === state.chatroom?.session_id

        if (payload?.from !== 'Falcon' || is_refresh) {
          const response = await api.getChatMessage({ session_id: state.table.selected?.session_id })
          if (response.data.success) {
            commit('SET_CHAT_CHATROOM_ITEMS', response.data.data)
          } else throw Error(response.data.message)
        }
      } catch (error) {
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      } finally {
        commit('SET_CHAT_CHATROOM_LOADING', false)
      }
    },

    async sendChat({ commit, dispatch, state }, payload) {
      try {
        commit('SET_CHAT_CHATROOM_SENDING', true)
        commit('APPEND_CHAT_CHATROOM_ITEMS', { sent_by_admin: true, message: payload.message, datetime: new Date().toISOString() })
        const response = await api.sentChatMessage({ params: payload })
        if (!response.data.success) throw Error(response.data.message)
      } catch (error) {
        dispatch('fetchChatMessage', state.chatroom.session_id)
        commit('SET_SNACKBAR', { status: true, color: 'error', text: error.message, icon: 'close', timeout: '2000' })
      } finally {
        commit('SET_CHAT_CHATROOM_SENDING', false)
      }
    }
  }
}
